import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
import { loginAccount, sendSms, smsLoginWithRedisToken } from "api";
import { useToast } from "components/Toast";

function Basic() {
  // 登录类型 0:账号密码 1:手机验证码
  const [loginType, setLoginType] = useState(0);

  // 账号密码
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // 手机验证码
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");

  const [error, setError] = useState("");
  const [canClick, setCanClick] = useState(true);

  // 验证码倒计时
  const [codeText, setCodeText] = useState("获取验证码");
  const [codeDisabled, setCodeDisabled] = useState(false);
  const [countDown, setCountDown] = useState(60);

  const showToast = useToast();
  const navigate = useNavigate();

  // 倒计时
  useEffect(() => {
    let timer = null;
    if (codeDisabled && countDown > 0) {
      timer = setInterval(() => {
        setCountDown((prev) => prev - 1);
      }, 1000);
      setCodeText(`${countDown}s`);
    } else if (countDown === 0) {
      setCodeDisabled(false);
      setCodeText("重新获取");
      setCountDown(60);
    }
    return () => clearInterval(timer);
  }, [codeDisabled, countDown]);

  // 清空错误 + 输入框
  const clearAll = () => {
    setError("");
    setEmail("");
    setPassword("");
    setPhone("");
    setCode("");
  };

  // 切换登录方式
  const switchLoginType = (type) => {
    setLoginType(type);
    clearAll();
  };

  // 获取短信验证码
  const handleGetCode = async () => {
    if (!phone) {
      setError("请输入手机号");
      return;
    }
    if (codeDisabled) return;

    const res = await sendSms({  phone });
    if (res?.status !== 0) {
      showToast(res?.msg || "发送失败", "error");
      return;
    }
    showToast("验证码已发送", "success");
    setCodeDisabled(true);
  };

  // 提交登录
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    if (!canClick) return;

    // 账号密码登录校验
    if (loginType === 0) {
      if (!email) {
        setError("请输入邮箱/账号");
        return;
      }
      if (!password) {
        setError("请输入密码");
        return;
      }
    } else {
      // 手机验证码登录校验
      if (!phone) {
        setError("请输入手机号");
        return;
      }
      if (!code) {
        setError("请输入验证码");
        return;
      }
    }

    setCanClick(false);
    let res;
    if (loginType === 0) {
      // 账号密码登录
      res = await loginAccount({
        tel: email,
        password: password,
      });
    } else {
      // 手机验证码登录
      res = await smsLoginWithRedisToken({
        tel: phone,
        vcode: code,
      });
    }

    if (res.status !== 0) {
      showToast(res.msg, "error");
      setCanClick(true);
      return;
    }

    localStorage.setItem("token", res.result.token);
    showToast("登录成功", "success");

    setTimeout(() => {
      navigate("/tables");
      setCanClick(true);
    }, 1000);
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            登 录
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {/* 切换标签 */}
          <MDBox display="flex" justifyContent="center" mb={3}>
            <MDButton
              variant={loginType === 0 ? "gradient" : "text"}
              color="info"
              onClick={() => switchLoginType(0)}
              sx={{ px: 3 }}
            >
              账号密码登录
            </MDButton>
            <MDButton
              variant={loginType === 1 ? "gradient" : "text"}
              color="info"
              onClick={() => switchLoginType(1)}
              sx={{ px: 3 }}
            >
              手机验证码登录
            </MDButton>
          </MDBox>

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            {/* 账号密码登录表单 */}
            {loginType === 0 ? (
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="text"
                    label="邮箱/账号"
                    fullWidth
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    error={Boolean(error)}
                    helperText={error}
                  />
                </MDBox>
                <MDBox mb={2}>
                  <MDInput
                    type="password"
                    label="密码"
                    fullWidth
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </MDBox>
              </>
            ) : (
              // 手机验证码登录表单
              <>
                <MDBox mb={2}>
                  <MDInput
                    type="tel"
                    label="手机号"
                    fullWidth
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    error={Boolean(error)}
                    helperText={error}
                  />
                </MDBox>
                <MDBox mb={2} display="flex" gap={1}>
                  <MDInput
                    type="text"
                    label="验证码"
                    fullWidth
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    sx={{ flex: 1 }}
                  />
                  <MDButton
                    variant="outlined"
                    color="info"
                    onClick={handleGetCode}
                    disabled={codeDisabled}
                    sx={{ whiteSpace: "nowrap" }}
                  >
                    {codeText}
                  </MDButton>
                </MDBox>
              </>
            )}

            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="gradient"
                color="info"
                fullWidth
                disabled={!canClick}
              >
                确认登录
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                忘记密码{" "}
                <MDTypography
                  component={Link}
                  to="/authentication/sign-up"
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                >
                  点此找回
                </MDTypography>
              </MDTypography>
            </MDBox>
          </MDBox>
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default Basic;